import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, OnChangeFn, PaginationState, VisibilityState } from '@tanstack/react-table';
import { Plus, ShieldAlert, TriangleAlert } from 'lucide-react';
import { PoolStatus, type Page, type PoolStatus as PoolStatusCode } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api, type QueryParams } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY, enumOptions, formatDate, type Option } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { toast } from '@/components/ui/toaster';
import { Board } from '@/components/data/Board';
import { DataTable } from '@/components/data/DataTable';
import { EnumBadge } from '@/components/data/EnumBadge';
import { FilterBar, type FilterDef, type FilterValues } from '@/components/data/FilterBar';
import { RateText } from '@/components/data/RateText';
import { ViewSwitcher } from '@/components/data/ViewSwitcher';
import { boardGroupsFor } from '@/components/data/tones';
import { useSavedViews } from '@/components/data/useSavedViews';
import { CandidateCard } from '@/pages/candidates/CandidateCard';
import { CandidateFormDialog } from '@/pages/candidates/CandidateFormDialog';
import { formatLanguages, useOptions, useSuppliers } from '@/pages/candidates/api';
import type { CandidateListItem } from '@/pages/candidates/types';

/** Query params the API accepts once (`z.string()`), unlike `poolStatus`. */
const SINGLE_VALUE_FILTERS = new Set(['skill', 'language', 'supplier', 'country', 'text']);
/** `sort` values the API understands (candidates.service SORTABLE); other columns sort client-side. */
const SERVER_SORTABLE = new Set([
  'createdAt',
  'candidateId',
  'fullName',
  'label',
  'poolStatus',
  'availableFrom',
  'rateExpectationEurH',
  'updatedAt',
]);
const PAGE_SIZE = 50;
const BOARD_PAGE_SIZE = 200;

const COLUMN_IDS = [
  'candidateId',
  'label',
  'poolStatus',
  'skills',
  'languages',
  'rateExpectationEurH',
  'availableFrom',
  'supplier',
  'consentExpiry',
] as const;

/** Enum filters are multi-select chips; keep only the newest choice for single-valued params. */
function singleValued(next: FilterValues, prev: FilterValues): FilterValues {
  const out: FilterValues = { ...next };
  for (const key of SINGLE_VALUE_FILTERS) {
    const v = out[key];
    if (Array.isArray(v) && v.length > 1) {
      const before = new Set(Array.isArray(prev[key]) ? (prev[key] as string[]) : []);
      const added = v.filter((x) => !before.has(x));
      out[key] = [added[0] ?? v[v.length - 1]!];
    }
  }
  return out;
}

/** FilterValues → query params (1:1; single-valued keys unwrap their array). */
function toListQuery(filters: FilterValues): QueryParams {
  const q: QueryParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || value === false) continue;
    q[key] = SINGLE_VALUE_FILTERS.has(key) && Array.isArray(value) ? value[0] : value;
  }
  return q;
}

function toOptions(values: readonly string[] | undefined): Option[] {
  return (values ?? []).map((v) => ({ value: v, label: v }));
}

const columns: ColumnDef<CandidateListItem, unknown>[] = [
  {
    id: 'candidateId',
    accessorKey: 'candidateId',
    header: 'ID',
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-mono text-xs">{row.original.candidateId}</span>
    ),
    size: 130,
  },
  {
    id: 'label',
    accessorFn: (c) => `${c.computed.displayName} ${c.label ?? ''}`.trim(),
    header: 'Name / label',
    cell: ({ row }) => (
      <div className="min-w-[10rem]">
        <div className="flex items-center gap-1.5 font-medium">
          {row.original.computed.displayName}
          {row.original.erasedAt ? (
            <Badge variant="destructive" className="px-1 py-0 text-[10px]">
              <ShieldAlert className="mr-0.5 h-3 w-3" aria-hidden="true" /> erased
            </Badge>
          ) : null}
        </div>
        {row.original.label ? (
          <div className="truncate text-xs text-muted-foreground">{row.original.label}</div>
        ) : null}
      </div>
    ),
  },
  {
    id: 'poolStatus',
    accessorKey: 'poolStatus',
    header: 'Pool status',
    cell: ({ row }) => <EnumBadge def={PoolStatus} value={row.original.poolStatus} />,
  },
  {
    id: 'skills',
    accessorFn: (c) => c.skills.join(', '),
    header: 'Skills',
    enableSorting: false,
    cell: ({ row }) => {
      const skills = row.original.skills;
      if (skills.length === 0) return <span className="text-muted-foreground">{EMPTY}</span>;
      const shown = skills.slice(0, 4);
      return (
        <ul className="flex flex-wrap gap-1" aria-label="Skills">
          {shown.map((s) => (
            <li key={s}>
              <Badge variant="secondary" className="rounded-sm px-1 py-0 text-[11px] font-normal">
                {s}
              </Badge>
            </li>
          ))}
          {skills.length > shown.length ? (
            <li className="text-[11px] leading-5 text-muted-foreground" title={skills.slice(4).join(', ')}>
              +{skills.length - shown.length}
            </li>
          ) : null}
        </ul>
      );
    },
  },
  {
    id: 'languages',
    accessorFn: (c) => formatLanguages(c.languages),
    header: 'Languages',
    enableSorting: false,
    cell: ({ row }) => <span className="whitespace-nowrap">{formatLanguages(row.original.languages)}</span>,
  },
  {
    id: 'rateExpectationEurH',
    accessorKey: 'rateExpectationEurH',
    header: 'Rate (EUR/h)',
    meta: { align: 'right' },
    cell: ({ row }) => <RateText rate={row.original.rateExpectationEurH} />,
  },
  {
    id: 'availableFrom',
    accessorKey: 'availableFrom',
    header: 'Available from',
    cell: ({ row }) =>
      row.original.availableFrom ? (
        <time dateTime={row.original.availableFrom} className="whitespace-nowrap">
          {formatDate(row.original.availableFrom)}
        </time>
      ) : (
        <span className="text-muted-foreground">{EMPTY}</span>
      ),
  },
  {
    id: 'supplier',
    accessorFn: (c) => c.supplier?.name ?? '',
    header: 'Supplier',
    cell: ({ row }) => row.original.supplier?.name ?? <span className="text-muted-foreground">{EMPTY}</span>,
  },
  {
    id: 'consentExpiry',
    accessorFn: (c) => c.computed.consentExpiry ?? '',
    header: 'Consent expiry',
    cell: ({ row }) => {
      const { consentExpiry, consentExpiresSoon } = row.original.computed;
      if (!consentExpiry) return <span className="text-muted-foreground">{EMPTY}</span>;
      return (
        <span
          className={
            consentExpiresSoon
              ? 'inline-flex items-center gap-1 whitespace-nowrap font-medium text-warning'
              : 'whitespace-nowrap'
          }
        >
          {consentExpiresSoon ? (
            <TriangleAlert className="h-4 w-4" aria-label="Consent expires soon" />
          ) : null}
          <time dateTime={consentExpiry}>{formatDate(consentExpiry)}</time>
        </span>
      );
    },
  },
];

export function CandidatesListPage() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const options = useOptions();
  const suppliers = useSuppliers();
  const sv = useSavedViews('CANDIDATE');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const filterDefs = React.useMemo<FilterDef[]>(
    () => [
      { key: 'text', label: 'Search', type: 'text', placeholder: 'Search id, name, label, skills…' },
      { key: 'poolStatus', label: 'Pool status', type: 'enum', options: enumOptions(PoolStatus) },
      {
        key: 'skill',
        label: 'Skill',
        type: 'enum',
        options: toOptions(options.data?.skill),
        placeholder: 'Skill…',
      },
      {
        key: 'language',
        label: 'Language',
        type: 'enum',
        options: toOptions(options.data?.language),
        placeholder: 'Language…',
      },
      {
        key: 'country',
        label: 'Country',
        type: 'enum',
        options: toOptions(options.data?.country),
        placeholder: 'Country…',
      },
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'enum',
        options: (suppliers.data ?? []).map((p) => ({ value: p.id, label: p.name })),
        placeholder: 'Supplier…',
      },
    ],
    [options.data, suppliers.data],
  );

  const isBoard = sv.state.layout === 'BOARD';
  const filtersKey = JSON.stringify(sv.state.filters);
  React.useEffect(() => {
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [filtersKey]);

  const sort = sv.state.sorts[0];
  const serverSort = sort && SERVER_SORTABLE.has(sort.id) ? sort : undefined;
  const query: QueryParams = {
    ...toListQuery(sv.state.filters),
    page: isBoard ? 1 : pagination.pageIndex + 1,
    pageSize: isBoard ? BOARD_PAGE_SIZE : pagination.pageSize,
    sort: serverSort?.id,
    order: serverSort ? (serverSort.desc ? 'desc' : 'asc') : undefined,
  };
  const listKey = queryKeys.list('candidates', query);
  const list = useQuery({
    queryKey: listKey,
    queryFn: () => api.get<Page<CandidateListItem>>('/candidates', { query }),
    placeholderData: keepPreviousData,
  });
  const items = React.useMemo(() => list.data?.items ?? [], [list.data]);
  const total = list.data?.total ?? 0;

  const setPoolStatus = useMutation({
    mutationFn: ({ id, poolStatus }: { id: string; poolStatus: PoolStatusCode }) =>
      api.patch(`/candidates/${id}`, { poolStatus }),
    onMutate: async ({ id, poolStatus }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<Page<CandidateListItem>>(listKey);
      if (previous) {
        queryClient.setQueryData<Page<CandidateListItem>>(listKey, {
          ...previous,
          items: previous.items.map((c) => (c.id === id ? { ...c, poolStatus } : c)),
        });
      }
      return { previous };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(listKey, ctx.previous);
      toast.error(e.message);
    },
    onSuccess: (_res, { poolStatus }) => toast.success(`Pool status → ${PoolStatus.label(poolStatus)}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['candidates'] }),
  });

  const visibility = React.useMemo<VisibilityState>(() => {
    if (sv.state.columns.length === 0) return {};
    const shown = new Set(sv.state.columns);
    const v: VisibilityState = {};
    for (const id of COLUMN_IDS) v[id] = shown.has(id);
    return v;
  }, [sv.state.columns]);
  const onVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next = typeof updater === 'function' ? updater(visibility) : updater;
    sv.setColumns(COLUMN_IDS.filter((id) => next[id] !== false));
  };

  const activeFilters = Object.keys(toListQuery(sv.state.filters)).length > 0;

  return (
    <>
      <PageHeader
        title="Candidates"
        description="The candidate pool: skills, languages, rate expectations and consent."
        actions={
          canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> New candidate
            </Button>
          ) : null
        }
      />
      <div className="space-y-3">
        <ViewSwitcher {...sv} filterDefs={filterDefs} canSave={canWrite} />
        <FilterBar
          filters={filterDefs}
          values={sv.state.filters}
          onChange={(values) => sv.setFilters(singleValued(values, sv.state.filters))}
        />
        {list.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {list.error instanceof Error ? list.error.message : 'Could not load candidates'}
          </p>
        ) : null}
        {isBoard ? (
          <>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {list.isLoading ? 'Loading…' : `${total} candidate${total === 1 ? '' : 's'}`}
              {total > BOARD_PAGE_SIZE
                ? ` · showing the first ${BOARD_PAGE_SIZE}; narrow the filters to see the rest`
                : ''}
            </p>
            <Board
              items={items}
              groups={boardGroupsFor(PoolStatus)}
              groupKey={(c) => c.poolStatus}
              getItemId={(c) => c.id}
              renderCard={(c) => <CandidateCard candidate={c} />}
              onMove={(id, _from, to) => setPoolStatus.mutate({ id, poolStatus: to as PoolStatusCode })}
              onCardClick={(c) => navigate(`/candidates/${c.id}`)}
              readOnly={!canWrite}
              label="Candidates by pool status"
            />
          </>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            isLoading={list.isLoading}
            rowHref={(c) => `/candidates/${c.id}`}
            sorting={sv.state.sorts}
            onSortingChange={(u) => sv.setSorts(typeof u === 'function' ? u(sv.state.sorts) : u)}
            columnVisibility={visibility}
            onColumnVisibilityChange={onVisibilityChange}
            manualPagination
            rowCount={total}
            pagination={pagination}
            onPaginationChange={setPagination}
            pageSizeOptions={[25, 50, 100, 200]}
            hideSearch
            caption="Candidates"
            emptyTitle="No candidates"
            emptyDescription={
              activeFilters
                ? 'No candidate matches the current filters.'
                : 'Register the first candidate with "New candidate".'
            }
          />
        )}
      </div>
      <CandidateFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(candidate) => navigate(`/candidates/${candidate.id}`)}
      />
    </>
  );
}
